import {
  ActionIcon,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  Menu,
  rem,
  ScrollArea,
  Text,
  Title,
  UnstyledButton
} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {
  adjustToTimeZone,
  endOfDay,
  flaggedMetersSelector,
  startOfDay,
  statisticsType,
  subDays,
  useSearchFlaggedMetersQuery
} from "@ots-frontend/ots-data";
import {
  IconChartDots3,
  IconChevronDown,
  IconDatabase,
  IconFileBarcode,
  IconGauge,
  IconLayersDifference,
  IconRipple
} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
  FlaggedMeterDataTable,
  HasAccess,
  InsightContainer,
  PAGINATION_PAGE_SIZES,
  PipelineInsightsGrid,
  PipelineStatisticsDataTable,
  ResizeableContent,
  ThemedPage,
  Tooltip,
  useApp,
} from "@ots-frontend/ots-ui";
import {useGetRecentPipelineInsights} from "../../components/hooks/useGetRecentPipelineInsights";

export type PipelinePageProps = {}

export const PipelinePage = (props: PipelinePageProps) => {

  const {t} = useTranslation(["translation", "common"]);
  const navigate = useNavigate()

  const {regionId, appCategory} = useApp();

  const filter = useMemo(() => ({
    fromDttm: startOfDay(subDays(new Date(), 4)).getTime(),
    toDttm: endOfDay(new Date()).getTime()
  }), [])

  const insights = useGetRecentPipelineInsights({
    fromDttm: filter.fromDttm,
    toDttm: filter.toDttm,
    regionId: regionId,
    category: appCategory,
  })

  const selectFlaggedMeters = useMemo(() => {
    return flaggedMetersSelector();
  }, []);

  const {flaggedMeters, isFlaggedMetersLoading, refetch: refetchFlaggedMeters} = useSearchFlaggedMetersQuery({
    fromDttm: adjustToTimeZone(filter.fromDttm).getTime(),
    toDttm: adjustToTimeZone(filter.toDttm).getTime(),
    regionIds: regionId ? [regionId] : undefined,
    categories: appCategory ? [appCategory.code] : undefined,
  }, {
    selectFromResult: (result) => ({
      ...result,
      flaggedMeters: selectFlaggedMeters(result),
      isFlaggedMetersLoading: result.isLoading,
    })
  })

  const {
    statistics: {
      data: statistics,
      isLoading: isStatisticsLoading,
      refetch: refetchStatistics
    }
  } = insights;

  const regionStats = useMemo(() => {
    return regionId ? statistics?.getRegionStats(regionId) : undefined
  }, [regionId, statistics])

  const pipelineStats = useMemo(() => {
    return statistics?.pipelineStats ?? []
  }, [statistics])

  const onRefreshClick = useCallback(() => {
    if (refetchStatistics) {
      refetchStatistics()
    }

    if (refetchFlaggedMeters) {
      refetchFlaggedMeters()
    }
  }, [refetchStatistics, refetchFlaggedMeters])

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <ScrollArea h={size.height} p="sm" offsetScrollbars={true}>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Group spacing="xs">
                      <IconChartDots3/>
                      <Title order={4}>
                        {t("page.pipeline.title", "Pipeline", {ns: "translation"})}
                      </Title>
                      <Tooltip label={"Pipeline Data"} withinPortal={true}>
                        <ActionIcon
                          variant="subtle"
                          onClick={() => navigate("data")}>
                          <IconDatabase size="1.125rem"/>
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip
                        label={t("common.entity.meterflowvolume.plural", "Meter Flow Volumes")}
                        withinPortal={true}>
                        <ActionIcon
                          variant="subtle"
                          onClick={() => navigate("meter-flows")}>
                          <IconGauge size="1.125rem"/>
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip
                        label={t("common.entity.pipelineflow.plural", "Flow Volumes")}
                        withinPortal={true}>
                        <ActionIcon
                          variant="subtle"
                          onClick={() => navigate("flows")}>
                          <IconRipple size="1.125rem"/>
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip
                        label={t("common.entity.pipelineflowtransport.plural", "Flow Tickets")}
                        withinPortal={true}>
                        <ActionIcon
                          variant="subtle"
                          onClick={() => navigate("flow-tickets")}>
                          <IconFileBarcode size="1.125rem"/>
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              Actions
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => onRefreshClick()}>
                          {t("actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <Menu.Divider/>
                        <Menu.Item onClick={() => navigate("insights/search")}>
                          {t("commons.shared.historical", "Historical", {ns: "common"})}
                        </Menu.Item>
                        <HasAccess permissions={["edit"]}>
                          <>
                            <Menu.Divider/>
                            <Menu.Item onClick={() => navigate("form")}>
                              {t("common.action.new", "New", {ns: "common"})}
                            </Menu.Item>
                          </>
                        </HasAccess>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Card>
                <Grid>
                  <Grid.Col span={12}>
                    <Group>
                      <Button
                        onClick={() => navigate("settlement")}
                        leftIcon={<IconLayersDifference/>}>
                        {t("common.page.pipelinesettlement", "Pipeline Settlement", {ns: "common"})}
                      </Button>
                    </Group>
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Divider my="sm"/>
                    <PipelineInsightsGrid
                      insights={regionStats}
                      rateModels={['Pipeline']}
                      category={appCategory}
                      loading={isStatisticsLoading}>

                      <Grid.Col span={12}>
                        <InsightContainer title={t<string>("common.insight.bypipeline", "By Pipeline")}>
                          <PipelineStatisticsDataTable
                            m="md"
                            withBorder
                            records={pipelineStats}
                            recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                            fetching={isStatisticsLoading}
                            types={[statisticsType.quantity, statisticsType.cost]}
                            category={appCategory}
                            regionId={regionId}
                            fromDttm={filter.fromDttm}
                            toDttm={filter.toDttm}/>
                        </InsightContainer>
                      </Grid.Col>

                      {flaggedMeters.length > 0 &&
                        <Grid.Col span={12}>
                          <InsightContainer
                            title={t<string>("common.insight.flaggedmeters", "Flagged Meters")}
                            icon={"flag"}>
                            <FlaggedMeterDataTable
                              m="md"
                              withBorder={true}
                              withColumnBorders={true}
                              records={flaggedMeters}
                              recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                              fetching={isFlaggedMetersLoading}/>
                          </InsightContainer>
                        </Grid.Col>
                      }

                    </PipelineInsightsGrid>
                  </Grid.Col>
                </Grid>
              </ScrollArea>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
